// MARK://dhk8/pos/features/billing/invoices-page
// Lista de facturas CFDI 4.0. Tabs: "Día actual" / "Histórico" / "Por cliente".
// Ported from sc-restaurant/apps/web/src/features/billing/InvoicesPage.tsx,
// adapted to dhk8-pos (DataTable mobile variant, CSS tokens, lucide icons).
//
// Wired to clients/invoices (stub-mode in dev — see clients/invoices.ts TODO).
// On row click → navigates to /billing/:id (BillingDetailPage). Route must be
// registered by sibling A — see App.tsx TODO.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { FilePlus2, Receipt } from 'lucide-react'

import { DataTable, DataTableToolbar, DataTableSearch, type ColumnDef } from '../../components/DataTable.js'
import { listInvoices, type InvoiceDto, type InvoiceFilters, type InvoiceStatus } from '../../lib/clients/invoices.js'
import { listCustomers, type CustomerDto } from '../../lib/clients/customers.js'
import { formatCents } from '../../lib/money.js'
import { StatusBadge } from './StatusBadge.js'
import { billingStyles } from './billing-styles.js'
import FacturarSheet from './FacturarSheet.js'

type Tab = 'today' | 'history' | 'by-customer'

const STATUS_OPTS: Array<{ value: InvoiceStatus | 'ALL'; label: string }> = [
  { value: 'ALL', label: 'Todos los estados' },
  { value: 'PENDING_STAMPING', label: 'Pendientes' },
  { value: 'STAMPED', label: 'Timbradas' },
  { value: 'CANCELLED', label: 'Canceladas' },
  { value: 'STAMPING_FAILED', label: 'Falló timbrado' },
]

function todayYmd(): string {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d.toISOString().slice(0, 10)
}

export default function InvoicesPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [tab, setTab] = useState<Tab>('today')
  const [facturarOpen, setFacturarOpen] = useState(false)

  // Deep link from a ticket: /billing?orderId=… opens the "Facturar" sheet.
  const orderIdFromQuery = searchParams.get('orderId')
  useEffect(() => {
    if (orderIdFromQuery) setFacturarOpen(true)
  }, [orderIdFromQuery])

  function closeFacturar() {
    setFacturarOpen(false)
    if (orderIdFromQuery) {
      const next = new URLSearchParams(searchParams)
      next.delete('orderId')
      setSearchParams(next, { replace: true })
    }
  }

  // Histórico filters
  const [status, setStatus] = useState<InvoiceStatus | 'ALL'>('ALL')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')

  // Por cliente
  const [customerSearch, setCustomerSearch] = useState('')
  const [customers, setCustomers] = useState<CustomerDto[]>([])
  const [customerId, setCustomerId] = useState<string>('')

  const [rows, setRows] = useState<InvoiceDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [textFilter, setTextFilter] = useState('')
  const [reloadTick, setReloadTick] = useState(0)

  const filters: InvoiceFilters = useMemo(() => {
    if (tab === 'today') return { fromDate: todayYmd(), toDate: todayYmd() }
    if (tab === 'by-customer') return customerId ? { customerId } : {}
    return {
      ...(status === 'ALL' ? {} : { status }),
      ...(fromDate ? { fromDate } : {}),
      ...(toDate ? { toDate } : {}),
    }
  }, [tab, status, fromDate, toDate, customerId])

  useEffect(() => {
    const ctrl = new AbortController()
    setLoading(true)
    setError(null)
    listInvoices(filters, ctrl.signal)
      .then((r) => setRows(r))
      .catch((e) => {
        // Ignora abortos del cleanup (StrictMode doble-montaje / cambio de filtros):
        // no son errores reales, la siguiente llamada resuelve. Antes pintaban
        // "signal is aborted without reason" por encima del resultado bueno.
        if (e?.name === 'AbortError' || ctrl.signal.aborted) return
        setError(e instanceof Error ? e.message : 'No se pudieron cargar las facturas')
      })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false) })
    return () => ctrl.abort()
  }, [filters, reloadTick])

  // Load customers when switching to the by-customer tab.
  useEffect(() => {
    if (tab !== 'by-customer') return
    const ctrl = new AbortController()
    listCustomers(customerSearch || undefined, ctrl.signal)
      .then((c) => setCustomers(c))
      .catch(() => setCustomers([]))
    return () => ctrl.abort()
  }, [tab, customerSearch])

  const columns: ColumnDef<InvoiceDto>[] = [
    { key: 'folio', label: 'Folio', sortable: true, sortValue: (r) => `${r.serie}${r.folio.padStart(8, '0')}` },
    { key: 'issuedAt', label: 'Fecha', sortable: true, sortValue: (r) => r.issuedAt, hideOnNarrow: true },
    { key: 'customer', label: 'Cliente' },
    { key: 'total', label: 'Total', numeric: true, align: 'end', sortable: true, sortValue: (r) => r.totalCents },
    { key: 'uuid', label: 'UUID', hideOnNarrow: true },
    { key: 'status', label: 'Estado' },
  ]

  const renderCell = (r: InvoiceDto, c: ColumnDef<InvoiceDto>) => {
    switch (c.key) {
      case 'folio':
        return `${r.serie}-${r.folio}${r.isGlobalDay ? ' · global' : ''}`
      case 'issuedAt':
        return new Date(r.issuedAt).toLocaleDateString('es-MX')
      case 'customer':
        return r.customer ? `${r.customer.legalName}${r.customer.rfc ? ` (${r.customer.rfc})` : ''}` : 'Público en general'
      case 'total':
        return <span className="dhk8-bil__money">{formatCents(r.totalCents)}</span>
      case 'uuid':
        return r.cfdiUuid ? <code style={{ fontSize: 'var(--pos-text-xxs, 11px)' }}>{r.cfdiUuid}</code> : '—'
      case 'status':
        return <StatusBadge status={r.status} />
      default:
        return null
    }
  }

  const visibleRows = useMemo(() => {
    const base = tab === 'by-customer' && !customerId ? [] : rows
    const q = textFilter.trim().toLowerCase()
    if (!q) return base
    return base.filter(
      (r) =>
        `${r.serie}-${r.folio}`.toLowerCase().includes(q) ||
        (r.cfdiUuid ?? '').toLowerCase().includes(q) ||
        (r.customer?.legalName ?? '').toLowerCase().includes(q) ||
        (r.customer?.rfc ?? '').toLowerCase().includes(q),
    )
  }, [rows, tab, customerId, textFilter])

  return (
    <div className="dhk8-bil">
      <header className="dhk8-bil__head">
        <div className="dhk8-bil__headrow">
          <h1 className="dhk8-bil__title">
            <Receipt size={18} className="dhk8-bil__title-ico" aria-hidden />
            Facturas CFDI
          </h1>
          <button type="button" className="dhk8-bil__btn dhk8-bil__btn--primary" onClick={() => setFacturarOpen(true)}>
            <FilePlus2 size={16} aria-hidden style={{ marginRight: 6, verticalAlign: '-3px' }} /> Facturar
          </button>
        </div>
        <p className="dhk8-bil__sub">Facturas 4.0 del día, histórico y por cliente. Cadena: DocketHub → Omnifiscal → FiscalAPI → SAT.</p>
      </header>

      <div className="dhk8-bil__tabs" role="tablist">
        <button role="tab" aria-current={tab === 'today'} className="dhk8-bil__tab" onClick={() => setTab('today')}>Día actual</button>
        <button role="tab" aria-current={tab === 'history'} className="dhk8-bil__tab" onClick={() => setTab('history')}>Histórico</button>
        <button role="tab" aria-current={tab === 'by-customer'} className="dhk8-bil__tab" onClick={() => setTab('by-customer')}>Por cliente</button>
      </div>

      {tab === 'history' && (
        <div className="dhk8-bil__filters">
          <label>Estado<select value={status} onChange={(e) => setStatus(e.target.value as InvoiceStatus | 'ALL')}>{STATUS_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select></label>
          <label>Desde<input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} /></label>
          <label>Hasta<input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} /></label>
        </div>
      )}

      {tab === 'by-customer' && (
        <div className="dhk8-bil__filters">
          <label>Buscar cliente<input type="search" placeholder="RFC o razón social" value={customerSearch} onChange={(e) => setCustomerSearch(e.target.value)} /></label>
          <label>Cliente<select value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            <option value="">— Selecciona un cliente —</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.legalName} ({c.rfc})</option>)}
          </select></label>
        </div>
      )}

      {error && <div className="dhk8-bil__error">{error}</div>}
      {tab === 'by-customer' && !customerId && !loading && <div className="dhk8-bil__notice">Selecciona un cliente para ver sus facturas.</div>}

      <DataTable
        columns={columns}
        rows={visibleRows}
        getRowKey={(r) => r.id}
        renderCell={renderCell}
        onRowClick={(r) => navigate(`/billing/${r.id}`)}
        loading={loading}
        pageSize={20}
        primaryKey="folio"
        ariaLabel="Facturas"
        emptyState={<span>{tab === 'today' ? 'Sin facturas emitidas hoy.' : 'Sin facturas para este criterio.'}</span>}
        toolbar={
          <DataTableToolbar>
            <DataTableSearch placeholder="Filtrar en pantalla…" value={textFilter} onChange={(e) => setTextFilter(e.target.value)} aria-label="Filtrar facturas" />
          </DataTableToolbar>
        }
      />

      <FacturarSheet
        open={facturarOpen}
        onClose={closeFacturar}
        orderId={orderIdFromQuery}
        onIssued={() => setReloadTick((t) => t + 1)}
      />

      <style>{billingStyles}</style>
      <style>{`.dhk8-bil__headrow{display:flex;align-items:center;justify-content:space-between;gap:var(--pos-space-3,12px);flex-wrap:wrap;}`}</style>
    </div>
  )
}
